import seedRandom from "seed-random";
import type {ColorData} from "./_types/ColorData";
import type {Design, DesignProperties} from "./_types/Design";
import type {DesignOverrides, PatternFillOverrides} from "./_types/DesignOverrides";
import type {PatternFillOptions} from "./_types/PatternFillOptions";
import {ReproducibleFrame} from "./_types/ReproducibleFrame";
import type {Scene} from "./_types/Scene";
import type {SceneObjectWithColorData} from "./_types/SceneObjectWithColorData";
import type {Size} from "./_types/Size";
import type {WebGLRenderObject} from "./_types/WebGLRenderObject";
import {WebGLRenderTarget} from "./_types/WebGLRenderTarget";
import { Color, rgb } from "./helpers/customColor";
import {getColorDataFromImage} from "./helpers/getColorDataFromImage";
import {getColorPaletteFromColorData} from "./helpers/getColorPaletteFromColorData";
import {getObjectCountFromDesign} from "./helpers/getObjectCountFromDesign";
import {getObjectPipelineFromDesign} from "./helpers/getObjectPipelineFromDesign";
import {getShuffledArray} from "./helpers/getShuffledArray";
import {ObjectCache} from "./helpers/ObjectCache";
import {WebGLContext} from "./helpers/webgl/WebGLContext";
import * as blitShaders from "./shaders/blit";
import * as blurShaders from "./shaders/blur";
import * as colorSwapShaders from "./shaders/colorSwap";
import * as defaultShaders from "./shaders/default";
import * as maskShaders from "./shaders/mask";
import * as postProcessShaders from "./shaders/postProcess";

const GL = () => WebGLRenderingContext;
const WHITE: Color = { mode: "rgb", r: 1, g: 1, b: 1 };
const TRANSPARENT: Color = { mode: "rgb", r: 1, g: 1, b: 1, alpha: 0 };

type WebGLRenderObjectsWithTextureIndex = WebGLRenderObject & { textureIndex: number };

export class MagicFrameEngine {

    private webgl: WebGLContext = new WebGLContext();

    private defaultProgram: WebGLProgram = this.webgl.createProgram({
        vertexShader: defaultShaders.vertex,
        fragmentShader: defaultShaders.fragment
    });

    private blitProgram: WebGLProgram = this.webgl.createProgram({
        vertexShader: defaultShaders.vertex,
        fragmentShader: blitShaders.fragment
    });

    private maskProgram: WebGLProgram = this.webgl.createProgram({
        vertexShader: defaultShaders.vertex,
        fragmentShader: maskShaders.fragment
    });

    private blurProgram: WebGLProgram = this.webgl.createProgram({
        vertexShader: defaultShaders.vertex,
        fragmentShader: blurShaders.fragment
    });

    private postProcessProgram: WebGLProgram = this.webgl.createProgram({
        vertexShader: defaultShaders.vertex,
        fragmentShader: postProcessShaders.fragment
    });

    private colorSwapProgram: WebGLProgram = this.webgl.createProgram({
        vertexShader: defaultShaders.vertex,
        fragmentShader: colorSwapShaders.fragment
    });

    private colorDataCache: ObjectCache<ColorData> = new ObjectCache();

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public render(scene: Scene, design: Design, seed?: string, overrides?: DesignOverrides): string {
        try {
            seedRandom(seed, {global: true});
            return this._render(scene, design, seed, undefined, overrides);
        } finally {
            seedRandom.resetGlobal();
        }
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public renderFromReproducibleFrame(scene: Scene, design: Design, reproducibleFrameString: string, overrides?: DesignOverrides): string {
        return this._render(scene, design, undefined, reproducibleFrameString, overrides);
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    private _render(scene: Scene, design: Design, seed?: string, reproducibleFrameString?: string, overrides?: DesignOverrides): string {

        // Apply overrides to create effective design
        const effectiveDesign = overrides
            ? this.applyOverrides(design, overrides)
            : design;

        const {
            webgl,
            blitProgram,
            colorSwapProgram,
            maskProgram,
            postProcessProgram
        } = this;

        // Render at 2x resolution for anti-aliasing
        const supersamplingFactor = 2;
        const renderWidth = scene.width * supersamplingFactor;
        const renderHeight = scene.height * supersamplingFactor;

        webgl.resize(renderWidth, renderHeight);

        // A front buffer (currentTarget) in graphics programming is the buffer (canvas) which is currently rendered to.
        // The back buffer (previousTarget) contains the previously rendered frame, using these buffers we could use previous
        // rendered frames to do post-processing on or use them as a mask.
        const highResScene = { width: renderWidth, height: renderHeight };
        let [currentTarget, previousTarget] = [
            webgl.createRenderTarget(highResScene, `__ct_${renderWidth}x${renderHeight}`),
            webgl.createRenderTarget(highResScene, `__pt_${renderWidth}x${renderHeight}`)
        ];

        // We swap the buffers to make sure the front and back buffer
        // are always what they should be by definition
        const swapTargets = () => [currentTarget, previousTarget] = [previousTarget, currentTarget];

        webgl.useRenderTarget(previousTarget);
        webgl.clear(TRANSPARENT);

        webgl.useRenderTarget(currentTarget);
        webgl.clear(TRANSPARENT);

        let parsedFrame: ReproducibleFrame | null = null;
        if (reproducibleFrameString) {
            try {
                parsedFrame = JSON.parse(reproducibleFrameString);
            } catch (e) {
                console.error('Failed to parse reproducible frame string', e);
            }
        }

        // Get objects at high resolution for anti-aliasing
        const supersampledSceneObjects = { ...scene, width: renderWidth, height: renderHeight, objects: scene.objects.map(o => ({
            ...o,
            x: o.x * supersamplingFactor,
            y: o.y * supersamplingFactor,
            width: o.width * supersamplingFactor,
            height: o.height * supersamplingFactor
        }))};

        const objects = parsedFrame
            ? this._getWebGLRenderObjectsFromParsedFrame(supersampledSceneObjects, effectiveDesign, parsedFrame, seed)
            : this._getWebGLRenderObjectsFromDesign(supersampledSceneObjects, effectiveDesign);

        const reproducibleFrame = {
            objects: objects.map(o => {
                const {texture, ...objectWithoutTexture} = o;
                return {
                    ...objectWithoutTexture,
                    x: o.x / supersamplingFactor,
                    y: o.y / supersamplingFactor,
                    width: o.width / supersamplingFactor,
                    height: o.height / supersamplingFactor
                };
            }),
            width: scene.width,
            height: scene.height
        };

        this._renderObjects(effectiveDesign, objects, currentTarget);

        // blur requires a different flow
        // since we need a blurred background of the images.
        const requiresBlurredBackground = effectiveDesign.properties.color?.mode === "blur";

        if (requiresBlurredBackground) {

            const blurredBackground = this._renderBlurredBackground(supersampledSceneObjects);

            swapTargets();
            webgl.useProgram(maskProgram);
            webgl.useRenderTarget(currentTarget);
            webgl.setUniform("mask", { type: GL().SAMPLER_2D, value: previousTarget.texture, textureIndex: 0 });
            webgl.render(createFullScreenRenderObjectWithTexture(highResScene, blurredBackground));
        }

        // Fill mode "frame" requires a flow which requires us to redraw the design frame,
        // this is because the frame fill mode consists of 1 image which should fill the whole frame.
        // This image should also include the colors of the sceneObject/sceneObjects,
        // this can only be done by redrawing the image frame and recoloring the colors present in the design image.
        const requiresColorSwapping = effectiveDesign.properties.fill.mode === "frame"
            && effectiveDesign.properties.color?.mode !== "blur";

        if (requiresColorSwapping) {

            const sceneObject = scene.objects[0];
            const cacheKey = sceneObject.key ?? sceneObject.image.src;
            if (!this.colorDataCache.has(cacheKey)) {
                this.colorDataCache.set(cacheKey, getColorDataFromImage(sceneObject.image));
            }

            const sceneObjectWithColorData = {
                ...sceneObject,
                data: this.colorDataCache.get(cacheKey)!
            };

            swapTargets();
            webgl.useProgram(colorSwapProgram);
            webgl.useRenderTarget(currentTarget);
            webgl.setUniform("palette_texture", {
                type: GL().SAMPLER_2D,
                textureIndex: 0,
                value: createPaletteTexture(
                    webgl,
                    sceneObjectWithColorData,
                    effectiveDesign.properties.color?.maxColors)
            });
            webgl.render(createFullScreenRenderObjectWithTexture(highResScene, previousTarget.texture));
        }

        const constraints = effectiveDesign.properties.color?.constraints;

        if (constraints) {

            swapTargets();
            webgl.useProgram(postProcessProgram);
            webgl.useRenderTarget(currentTarget);
            webgl.setUniform("lightness_min", {type: GL().FLOAT, value: constraints.lightness.min});
            webgl.setUniform("lightness_max", {type: GL().FLOAT, value: constraints.lightness.max});
            webgl.setUniform("saturation_min", {type: GL().FLOAT, value: constraints.saturation.min});
            webgl.setUniform("saturation_max", {type: GL().FLOAT, value: constraints.saturation.max});
            webgl.render(createFullScreenRenderObjectWithTexture(highResScene, previousTarget.texture));
        }


        // Create a final render target at the original resolution for downsampling
        const finalTarget = webgl.createRenderTarget(scene, `__final_${scene.width}x${scene.height}`);

        swapTargets();
        webgl.setPremultipliedAlpha(true);
        webgl.useProgram(blitProgram);

        // First downsample to the final resolution target
        webgl.useRenderTarget(finalTarget);
        webgl.clear(WHITE);
        webgl.resize(scene.width, scene.height);
        webgl.render(createFullScreenRenderObjectWithTexture(scene, previousTarget.texture));

        // Then render to the screen
        webgl.useRenderTarget();
        webgl.clear(WHITE);
        webgl.render(createFullScreenRenderObjectWithTexture(scene, finalTarget.texture));

        webgl.setPremultipliedAlpha(false);

        return JSON.stringify(reproducibleFrame);
    }
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    private _renderObjects(
        design: Design,
        objects: Array<WebGLRenderObject>,
        renderTarget?: WebGLRenderTarget
    ): void {

        const {
            webgl,
            blitProgram,
            defaultProgram
        } = this;

        // Fill mode "frame" requires a flow which requires us to redraw the design frame,
        // this is because the frame fill mode consists of 1 image which should fill the whole frame.
        // This image should also include the colors of the sceneObject/sceneObjects,
        // this can only be done by redrawing the image frame and recoloring the colors present in the design image.
        const requiresColorSwapping = design.properties.fill.mode === "frame"
            && design.properties.color?.mode !== "blur";

        if (requiresColorSwapping) {
            webgl.useProgram(blitProgram);
        } else {
            webgl.setPremultipliedAlpha(true);
            webgl.useProgram(defaultProgram);
        }

        webgl.useRenderTarget(renderTarget);
        webgl.render(objects);

        webgl.setPremultipliedAlpha(false);
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    private _getWebGLRenderObjectsFromDesign(
        scene: Scene,
        design: Design
    ): Array<WebGLRenderObjectsWithTextureIndex> {

        const { webgl } = this;

        const objectCount = getObjectCountFromDesign(scene, design);
        const objects = Array.from({ length: objectCount }, () => {
            const imageIndex = Math.floor(Math.random() * design.images.length);
            return {
                ...createRenderObjectWithImage(webgl, design.images[imageIndex]),
                textureIndex: imageIndex
            };
        });

        const objectPipeline = getObjectPipelineFromDesign(scene, design);

        const sceneObjectsWithColorData = scene.objects.map(o => {
            const cacheKey = o.key ?? o.image.src;
            if (!this.colorDataCache.has(cacheKey)) {
                this.colorDataCache.set(cacheKey, getColorDataFromImage(o.image));
            }

            return {
                ...o,
                data: this.colorDataCache.get(cacheKey)!
            };
        });

        objectPipeline.apply(objects, sceneObjectsWithColorData);

        return objects;
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    private _getWebGLRenderObjectsFromParsedFrame(
        scene: Scene,
        design: Design,
        parsedFrame: ReproducibleFrame,
        seed?: string
    ): Array<WebGLRenderObjectsWithTextureIndex> {
        return this._getWebGLRenderObjectsFromReproducibleFrame(
            scene, design, parsedFrame as ReproducibleFrame
        );
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    private _getWebGLRenderObjectsFromReproducibleFrame(
        scene: Scene,
        design: Design,
        reproducibleFrame: ReproducibleFrame
    ): Array<WebGLRenderObjectsWithTextureIndex> {

        const { webgl } = this;

        // Calculate scaling factors to adapt to current resolution
        // IMPORTANT: This treats the stored dimensions as logical units (like SVG viewBox), not physical pixels
        // This makes the reproduction resolution-independent as long as aspect ratio is preserved
        const scaleX = scene.width / reproducibleFrame.width;
        const scaleY = scene.height / reproducibleFrame.height;

        return reproducibleFrame.objects.map(o => {
            return {
                ...o,
                x: o.x * scaleX,
                y: o.y * scaleY,
                width: o.width * scaleX,
                height: o.height * scaleY,
                textureIndex: o.textureIndex,
                texture: webgl.createTexture(design.images[o.textureIndex])
            };
        });
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    private _renderBlurredBackground(scene: Scene): WebGLTexture {

        const {
            webgl,
            blitProgram,
            blurProgram
        } = this;

        // see comment at other currentTarget and previousTarget variables
        let [currentTarget, previousTarget] = [
            webgl.createRenderTarget(scene, `__ct_blur_${scene.width}x${scene.height}`),
            webgl.createRenderTarget(scene, `__pt_blur_${scene.width}x${scene.height}`)
        ];

        const swapTargets = () => [currentTarget, previousTarget] = [previousTarget, currentTarget];

        webgl.setPremultipliedAlpha(true);
        webgl.useProgram(blitProgram);
        webgl.useRenderTarget(currentTarget);

        const blurObjects: Array<WebGLRenderObject> = [];
        for (const sceneObject of scene.objects) {
            for (let i = 0; i < 9; i++) {

                const column = i % 3;
                const row = Math.floor(i / 3);

                const flipX = (column & 1) === 0;
                const flipY = (row & 1) === 0;

                blurObjects.push({
                    x: sceneObject.x - sceneObject.width / 2 + column * sceneObject.width,
                    y: sceneObject.y - sceneObject.height / 2 + row * sceneObject.height,
                    color: WHITE,
                    width: flipX ? -sceneObject.width : sceneObject.width,
                    height: flipY ? -sceneObject.height : sceneObject.height,
                    rotation: 0,
                    texture: webgl.createTexture(sceneObject.image)
                });
            }
        }

        webgl.render(blurObjects);
        webgl.setPremultipliedAlpha(false);

        swapTargets();
        webgl.useProgram(blurProgram);
        webgl.useRenderTarget(currentTarget);
        webgl.render(createFullScreenRenderObjectWithTexture(scene, previousTarget.texture));

        return currentTarget.texture;
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public getCanvas(): HTMLCanvasElement {
        return this.webgl.getCanvas();
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public dispose(): void {
        this.webgl.dispose();
        this.colorDataCache.dispose();
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    private applyOverrides(design: Design, overrides: DesignOverrides): Design {
        const mergedProperties: DesignProperties = {
            ...design.properties
        };

        // Only apply fill overrides if the original fill property exists
        if (overrides.fill && design.properties.fill) {
            mergedProperties.fill = {
                mode: overrides.fill.mode ?? design.properties.fill.mode,
                randomize: overrides.fill.randomize ?? design.properties.fill.randomize
            };

            if (mergedProperties.fill.mode === "pattern") {
                if (overrides.fill.options && design.properties.fill.options) {
                    mergedProperties.fill.options = this.mergePatternOptions(
                        design.properties.fill.options,
                        overrides.fill.options
                    );
                } else {
                    // Keep original options if no override options provided or no original options
                    mergedProperties.fill.options = design.properties.fill.options;
                }
            } else {
                mergedProperties.fill.options = undefined;
            }
        }

        // Only apply scale overrides if the original scale property exists
        if (overrides.scale && design.properties.scale) {
            mergedProperties.scale = {
                width: overrides.scale.width ?? design.properties.scale.width,
                height: overrides.scale.height ?? design.properties.scale.height,
                randomize: overrides.scale.randomize ?? design.properties.scale.randomize
            };
        }

        // Only apply rotate overrides if the original rotate property exists
        if (overrides.rotate && design.properties.rotate) {
            mergedProperties.rotate = {
                amount: overrides.rotate.amount ?? design.properties.rotate.amount,
                randomize: overrides.rotate.randomize ?? design.properties.rotate.randomize
            };
        }

        // Only apply color overrides if the original color property exists
        if (overrides.color && design.properties.color) {
            const baseColor = design.properties.color;
            const mergedColor = {
                ...baseColor,
                ...overrides.color
            };

            if (overrides.color.constraints && baseColor.constraints) {
                mergedColor.constraints = {
                    lightness: {
                        ...baseColor.constraints.lightness,
                        ...(overrides.color.constraints.lightness || {})
                    },
                    saturation: {
                        ...baseColor.constraints.saturation,
                        ...(overrides.color.constraints.saturation || {})
                    }
                };
            } else if (overrides.color.constraints && !baseColor.constraints) {
                // Don't apply constraints override if base doesn't have constraints
                mergedColor.constraints = baseColor.constraints;
            }

            mergedProperties.color = mergedColor as any;
        }

        return {
            ...design,
            properties: mergedProperties
        };
    }

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    private mergePatternOptions(
        base: PatternFillOptions,
        overrides: PatternFillOverrides
    ): PatternFillOptions {
        return {
            width: overrides.width ?? base.width,
            height: overrides.height ?? base.height,
            spacing: {
                x: overrides.spacing?.x ?? base.spacing.x,
                y: overrides.spacing?.y ?? base.spacing.y
            },
            pattern: overrides.pattern ?? base.pattern
        };
    }
}

const createPaletteTexture = (
    webgl: WebGLContext,
    sceneObject?: SceneObjectWithColorData,
    maxColors?: number
): WebGLTexture => {

    if (!sceneObject) {
        return webgl.createTexture({
            width: 1,
            height: 1,
            data: new Uint8Array([0, 0, 0, 255 ])
        }, "__black_pixel");
    }

    if (maxColors && !sceneObject.data.palettes[maxColors]) {
        sceneObject.data.palettes[maxColors] = getColorPaletteFromColorData(sceneObject.data, maxColors);
    }

    const colors = getShuffledArray(maxColors
        ? sceneObject.data.palettes[maxColors]
        : sceneObject.data.uniqueColors).slice(0, 255);

    const nonLinearRgbColors = colors.map(c => {
        // @ts-ignore
        const {r, g, b} = rgb(c);
        return [r * 255, g * 255, b * 255, 255];
    });

    const paletteTextureKey = `__palette_${maxColors}_${nonLinearRgbColors.join(",")}`;

    return webgl.createTexture({
        width: colors.length,
        height: 1,
        data: new Uint8Array(nonLinearRgbColors.flat())
    }, paletteTextureKey);
};

const createRenderObjectWithImage = (
    webgl: WebGLContext,
    image: HTMLImageElement
): WebGLRenderObject => {
    return {
        x: 0,
        y: 0,
        color: { mode: "rgb", r: 1, g: 1, b: 1 },
        width: image.width,
        height: image.height,
        rotation: 0,
        texture: webgl.createTexture(image)
    };
};

const createFullScreenRenderObjectWithTexture = (
    screenSize: Size,
    texture: WebGLTexture
): WebGLRenderObject => {
    return {
        x: screenSize.width / 2,
        y: screenSize.height / 2,
        color: { mode: "rgb", r: 1, g: 1, b: 1 },
        width: screenSize.width,
        height: -screenSize.height,
        rotation: 0,
        texture
    };
};